import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { teamApi } from '@core/api';
import type { Actor } from '@core/types/actor';
import type { Entity } from '@core/types/entity';
import { Button, Alert } from '@react/components';
import { EntityAvatar } from '@/components/EntityAvatar';
import { Tooltip } from '@/components/Tooltip';
import { useOpenDirectConversation } from '@/features/chat/useOpenDirectConversation';
import { errMsg } from '@/features/task/panelHelpers';
import { cachedProfile, loadProfileReference, referenceScope, type ProfileReference } from './cache';

type ActorLike = Partial<Omit<Actor, 'entity'>> & { entity?: Partial<Entity>; id?: string; name?: string };
export interface ActorReferenceProps {
  actor?: ActorLike | null;
  id?: string | null;
  kind?: string;
  displayName?: string;
  className?: string;
  showAvatar?: boolean;
  fallbackLabel?: string;
  teamId?: string;
}

function ProfileActions({ reference }: { reference: ProfileReference }) {
  const { t } = useI18n();
  const dm = useOpenDirectConversation();
  const profile = reference.profile;
  return <div className="space-y-3">
    <div className="flex items-start gap-3">
      <EntityAvatar name={reference.name} resource={profile?.avatar} size="lg" />
      <div className="min-w-0">
        <p className="font-semibold break-words">{reference.name}</p>
        {profile?.username && <p className="text-xs text-muted-foreground break-words">@{profile.username}</p>}
        {profile?.location && profile.privacy_settings?.show_location !== false && <p className="mt-1 text-xs text-muted-foreground break-words">{profile.location}</p>}
      </div>
    </div>
    {profile?.bio && <p className="line-clamp-3 whitespace-pre-wrap break-words text-sm">{profile.bio}</p>}
    {reference.profileId && <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
      <Link to={`/userprofiles/${reference.profileId}`} className="text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">{t('references.profile')}</Link>
      <Button size="sm" variant="secondary" isLoading={dm.isOpening} onClick={() => { void dm.open(reference.profileId!); }}>{t('references.message')}</Button>
    </div>}
    {dm.error ? <Alert variant="error">{errMsg(dm.error, t('references.messageFailed'))}</Alert> : null}
  </div>;
}

export const ActorReference = observer(function ActorReference({ actor, id: explicitId, kind: explicitKind, displayName, className = '', showAvatar = true, fallbackLabel, teamId }: ActorReferenceProps) {
  const { t } = useI18n();
  const id = explicitId || actor?.entity?.id || actor?.id || '';
  const kind = (explicitKind || actor?.entity?.type_name || 'profile').replace(/^core\./, '').replace('userprofile', 'profile');
  const person = kind === 'profile' || kind === 'user';
  const scope = referenceScope();
  const key = `${scope}|${kind}|${id}`;
  const [resolved, setResolved] = useState<{ key: string; value: ProfileReference | null }>();
  const [group, setGroup] = useState<{ key: string; name: string; description: string; href?: string }>();
  const profile = resolved?.key === key ? resolved.value : (person ? cachedProfile(id, scope) : undefined);
  useEffect(() => {
    if (!id || !person) return;
    let active = true;
    void loadProfileReference(id, scope).then((value) => { if (active) setResolved({ key, value }); });
    return () => { active = false; };
  }, [id, key, person, scope]);
  useEffect(() => {
    if (!id || person) return;
    let active = true;
    if (kind === 'team') {
      teamApi.GetTeam(id).then((response) => {
        if (active && response.team) setGroup({ key, name: response.team.name, description: response.team.description, href: `/teams/${id}` });
      }).catch(() => { /* No cross-scope fallback. */ });
    } else if (kind === 'role' && teamId) {
      teamApi.ListTeamRoles(teamId).then((response) => {
        const role = response.roles?.roles?.find((item) => item.entity?.id === id);
        if (active && role) setGroup({ key, name: role.name, description: role.description, href: `/teams/${teamId}/roles/${id}` });
      }).catch(() => { /* A role without a readable team is not a profile. */ });
    }
    return () => { active = false; };
  }, [id, key, kind, person, teamId]);
  if (!id && !displayName && !actor?.display_name && !actor?.name) return <span className={className}>—</span>;
  const supplied = displayName || actor?.display_name || actor?.name || '';
  const safeName = supplied === id || /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(supplied) ? '' : supplied;
  const groupValue = group?.key === key ? group : undefined;
  const label = (profile?.name && profile.name !== '?' ? profile.name : '') || groupValue?.name || safeName || fallbackLabel || t(person ? 'references.unavailableProfile' : 'references.actor');
  const href = person ? (profile?.profileId ? `/userprofiles/${profile.profileId}` : undefined) : groupValue?.href;
  const card = person
    ? (profile?.profileId ? <ProfileActions reference={profile} /> : <div className="space-y-2"><p className="font-medium break-words">{label}</p><p className="text-sm text-muted-foreground">{t('references.profileNotAvailable')}</p></div>)
    : <div className="space-y-2"><p className="font-semibold break-words">{label}</p><p className="text-xs text-muted-foreground">{t(`references.kinds.${kind}`, { defaultValue: t('references.actor') })}</p>{groupValue?.description && <p className="line-clamp-3 text-sm">{groupValue.description}</p>}{href && <Link to={href} className="text-primary hover:underline">{t('references.open')}</Link>}</div>;
  const contents = <>{showAvatar && <EntityAvatar name={label} image={actor?.avatar} resource={profile?.profile?.avatar} size="xs" className="h-5 w-5 text-[9px]" />}<span className="truncate">{label}</span></>;
  const triggerClass = `inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-sm align-middle text-primary hover:underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`;
  return <Tooltip label={label} side="bottom" className="max-w-full min-w-0 align-middle" content={card}>
    {href ? <Link className={triggerClass} to={href} onClick={(event) => event.stopPropagation()} aria-haspopup="dialog">{contents}</Link> : <button type="button" className={triggerClass} onClick={(event) => event.stopPropagation()} aria-haspopup="dialog">{contents}</button>}
  </Tooltip>;
});
